import { EgpSeason, EgpService } from '../egp/egp.service';

import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';

import { TranslateService } from '@ngx-translate/core';

import { IconName } from '@fortawesome/fontawesome-svg-core';

import { environment } from '../../environments/environment';

import moment from 'moment';
import { Moment } from 'moment';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export interface EgpRegion {
  description: string;
  id: string;
  name: string;
  station: {};
  superArea: {};
}

export interface DataWarning {
  warningResponses: [
    {
      message: string;
    },
  ];
}

export enum WarnSeason {
  SUMMER = 'summer',
  WINTER = 'winter',
}

export interface WarnRegion {
  id: string;
  name: string;
  season: WarnSeason;
}
export interface WarnRegions {
  summer: Array<WarnRegion>;
  winter: Array<WarnRegion>;
}

export interface Warning {
  type: string;
  runtime: number;
  dtg: Moment;
  symbol: IconName;
  timelineClass: string;
  color: string;
  message: string;
}

export interface WarningData {
  region: WarnRegion;
  warning: Warning;
}

@Injectable({
  providedIn: 'root',
})
export class WarningService {
  private http = inject(HttpClient);
  private translate = inject(TranslateService);
  private egpService = inject(EgpService);

  private url = environment.url.bff + '/';
  warningMessage = {};

  regionControl = new UntypedFormControl();
  warnRegions = signal<WarnRegions>({
    summer: [],
    winter: [],
  });
  selectedRegion = signal<WarnRegion>(undefined);
  seasons = signal<Array<WarnSeason>>([]);
  isLoading = true;
  warningData: Array<Warning> = [];
  allWarningData = signal({
    summer: {},
    winter: {},
  });
  sortedWarningData = signal<Array<WarningData>>([]);
  hasWarningProducts = {
    summer: 0,
    winter: 0,
  };

  numberOfWarnings = 20;

  private getTranslations(): void {
    for (const translationKey of ['AUTOENTWARN', 'NOWARN']) {
      this.warningMessage[translationKey] = this.translate.instant(
        'PRODUCT.WARNING.Active.' + translationKey
      );
    }
  }

  public getWarning(warnRegion: WarnRegion, limit?: number): Observable<any> {
    this.getTranslations();
    if (!limit) {
      limit = 5;
    }
    const chunk = warnRegion.season === WarnSeason.SUMMER ? 'product/' : 'egp/';
    const fullUrl =
      this.url + 'warning/' + chunk + warnRegion.id + '/list?limit=' + limit;
    return this.http.get<DataWarning>(fullUrl).pipe(
      map(data => {
        const warnings: Array<Warning> = [];
        const warningsShown: Array<Warning> = [];
        let count = 0;
        for (const warning of data.warningResponses) {
          const runTime = warning['runTime'];
          let warnMessage = warning['message'].split('\\n')[0];
          if (warnMessage.indexOf('Vorabinformation') > -1) {
            const messages = warning['message'].split('\\n\\n\\n\\n')[0];
            warnMessage = messages.split('\\n').join();
          }
          const dtgStart = moment(warning['createDtg']);
          const today = moment().format('YYYY-MM-DD');

          // not older than 1 month for limited view
          if (limit < 10 && dtgStart.isBefore(moment().subtract(1, 'month'))) {
            continue;
          }
          if (limit < 30 && dtgStart.isBefore(moment().subtract(3, 'month'))) {
            continue;
          }
          const oneMonth = moment(dtgStart)
            .add(1, 'months')
            .format('YYYY-MM-DD');
          if (oneMonth < today && limit < 20 && warnings.length < 1) {
            count++;
            warnings.push({
              type: 'NOWARN',
              runtime: runTime,
              dtg: moment(),
              symbol: 'check',
              timelineClass: 'warnings-right',
              color: 'success',
              message: 'PRODUCT.WARNING.Active.NOWARN',
            });
            break;
          } else {
            if (runTime === 0) {
              // active warning
              warnings.push({
                type: 'WARN',
                runtime: runTime,
                dtg: dtgStart,
                symbol: 'exclamation-triangle',
                timelineClass: 'warnings-left',
                color: 'danger',
                message: warnMessage,
              });
            } else if (runTime < 0) {
              // entwarnung
              warnings.push({
                type: 'ENTWARN',
                runtime: runTime,
                dtg: dtgStart,
                symbol: 'check',
                timelineClass: 'warnings-right',
                color: 'success',
                message: warnMessage,
              });
            } else if (runTime === 24) {
              // vorabinformation
              warnings.push({
                type: 'VORAB',
                runtime: runTime,
                dtg: dtgStart,
                symbol: 'exclamation-triangle',
                timelineClass: 'warnings-left',
                color: 'warning',
                message: warnMessage,
              });
            } else {
              // autowarn
              warnings.push({
                type: 'WARN',
                runtime: runTime,
                dtg: dtgStart,
                symbol: 'exclamation-triangle',
                timelineClass: 'warnings-left',
                color: 'danger',
                message: warnMessage,
              });
            }
          }
        }
        for (let i = 0; i < warnings.length; i++) {
          const warnAfter = warnings[i - 1];
          const warn = warnings[i];
          const dtgEnd = moment(warn.dtg).add(warn.runtime, 'hours');
          if (warnAfter !== undefined) {
            if (warn.type === 'NOWARN' && warnAfter.type === 'NOWARN') {
              continue;
            } else if (warn.type === 'WARN' && warnAfter.type === 'ENTWARN') {
              warningsShown.push(warnings[i]);
              continue;
            } else if (warn.runtime === 24 && warnAfter.dtg > dtgEnd) {
              // auto entwarn
              warningsShown.push({
                type: 'AUTOENTWARN',
                dtg: dtgEnd,
                symbol: 'check',
                timelineClass: 'warnings-right',
                color: 'success',
                message: 'PRODUCT.WARNING.Active.AUTOENTWARN',
                runtime: null,
              });
            } else if (warn.runtime > 0 && warnAfter.dtg > dtgEnd) {
              // auto entwarn
              warningsShown.push({
                type: 'AUTOENTWARN',
                dtg: dtgEnd,
                symbol: 'check',
                timelineClass: 'warnings-right',
                color: 'success',
                message: 'PRODUCT.WARNING.Active.NOWARN',
                runtime: null,
              });
            }
          } else if (i === 0) {
            if (
              warn.runtime > 0 &&
              warn.runtime < 24 &&
              dtgEnd.isBefore(moment())
            ) {
              // auto entwarn
              warningsShown.push({
                type: 'AUTOENTWARN',
                dtg: dtgEnd,
                symbol: 'check',
                timelineClass: 'warnings-right',
                color: 'success',
                message: 'PRODUCT.WARNING.Active.NOWARN',
                runtime: null,
              });
            }
          }
          warningsShown.push(warnings[i]);
        }
        if (warnings.length < 1) {
          warningsShown.push({
            type: 'NOWARN',
            dtg: moment(),
            symbol: 'check',
            timelineClass: 'warnings-right',
            color: 'success',
            message: 'PRODUCT.WARNING.Active.NOWARN',
            runtime: null,
          });
        }
        if (count === limit) {
          return warningsShown.slice(0, 1);
        } else {
          return warningsShown.slice(0, limit);
        }
      })
    );
  }

  fetchWarningData(region: WarnRegion): Promise<void> {
    return new Promise((resolve, reject) => {
      this.getWarning(region, this.numberOfWarnings).subscribe(
        data => {
          this.allWarningData.update(allWarningData => {
            allWarningData[region.season][region.id] = data;
            return { ...allWarningData };
          });
          resolve();
        },
        error1 => reject(error1)
      );
    });
  }

  async extractWarnRegionsSummer(
    egwProducts: Array<any>,
    specificRegion = 0
  ): Promise<void> {
    return new Promise(async resolve => {
      const validRegions: Array<WarnRegion> = [];
      for (const product of egwProducts) {
        const point = product['points'][0];
        const validRegion = {
          id: product['id'] + '',
          name: point['searchName'],
          season: WarnSeason.SUMMER,
        };
        validRegions.push(validRegion);
        if (!specificRegion || specificRegion === product['id']) {
          await this.fetchWarningData(validRegion);
        }
      }
      this.warnRegions.update(warnRegions => {
        return {
          ...warnRegions,
          summer: validRegions.sort((a, b) => (a.name > b.name ? 1 : -1)),
        };
      });
      resolve();
    });
  }

  async extractWarnRegionsWinter(
    egwProducts: Array<any>,
    specificRegion: string = null
  ): Promise<void> {
    return new Promise(async (resolve, reject) => {
      this.egpService.getAllEgpAreas(EgpSeason.WINTER).then(
        async allRegions => {
          const validRegions: Array<WarnRegion> = [];
          for (const product of egwProducts) {
            for (const region of allRegions) {
              if (region.id === product['areaId'] + '') {
                const validRegion = {
                  id: region.id,
                  name: region.name,
                  season: WarnSeason.WINTER,
                };
                if (
                  validRegions.filter(
                    r =>
                      r.id === validRegion.id && r.season === validRegion.season
                  ).length === 0
                ) {
                  validRegions.push(validRegion);
                  if (!specificRegion || specificRegion === region.id) {
                    await this.fetchWarningData(validRegion);
                  }
                }
              }
            }
          }
          this.warnRegions.update(warnRegions => {
            return {
              ...warnRegions,
              winter: validRegions.sort((a, b) => (a.name > b.name ? 1 : -1)),
            };
          });

          resolve();
        },
        error1 => {
          console.error('Cannot fetch egw winter regions ', error1);
          reject(error1);
        }
      );
    });
  }
}
